import { ChevronRight, Settings } from "lucide-react";
import type { UseQueryResult } from "@tanstack/react-query";
import PageRoutes from "@/utils/constants";
import { Button } from "@/v2/components/ui/button";
import { Checkbox } from "@/v2/components/ui/checkbox";
import { Avatar, AvatarFallback, AvatarImage } from "@/v2/components/ui/avatar";
import type { Channel, FavoriteTeam, ScheduleGame, SearchFilters } from "./api";
import { ScheduleTable } from "./ScheduleTable";

export type SearchResultsPanelProps = {
  /** Distinguishes generated element ids between the desktop and mobile instances. */
  idPrefix: string;
  /** Two-column on desktop (results | filters); stacked single column on mobile. */
  layout: "desktop" | "mobile";
  filters: SearchFilters;
  selectedTeams: string[];
  selectedChannels: string[];
  toggleFilter: (key: "team" | "channel", id: string) => void;
  liveQuery: UseQueryResult<ScheduleGame[]>;
  vodQuery: UseQueryResult<ScheduleGame[]>;
  channelsQuery: UseQueryResult<Channel[]>;
  favoritesQuery: UseQueryResult<FavoriteTeam[]>;
  isAuthenticated: boolean;
  limit: number;
  onSeeAll: (route: string) => void;
  onManageFavorites: () => void;
};

/**
 * The results/filters content shown inside both the desktop and mobile
 * header search dropdowns: Live and Upcoming, On Demand, Favorites, and
 * Channels. Desktop and mobile only differ in their trigger and the
 * container around this panel; the query wiring lives in
 * useDebouncedScheduleSearch and the rendering here is identical.
 */
export function SearchResultsPanel({
  idPrefix,
  layout,
  filters,
  selectedTeams,
  selectedChannels,
  toggleFilter,
  liveQuery,
  vodQuery,
  channelsQuery,
  favoritesQuery,
  isAuthenticated,
  limit,
  onSeeAll,
  onManageFavorites,
}: SearchResultsPanelProps) {
  return (
    <div className="v2-search-results flex flex-col">
      <div
        className={`grid grid-cols-1 ${
          layout === "desktop" ? "sm:grid-cols-[1fr_280px]" : ""
        }`}
      >
        {/* Left: schedule results */}
        <div className="flex flex-col gap-4 bg-popover p-4">
          <section>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-base font-semibold text-foreground">Live and Upcoming</h3>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs text-action hover:bg-action hover:text-action-hover"
                onClick={() => onSeeAll(PageRoutes.UpcomingEvent)}
              >
                See All
                <ChevronRight className="ml-0.5 size-3" />
              </Button>
            </div>
            <ScheduleTable
              games={liveQuery.data ?? []}
              isLoading={liveQuery.isLoading}
              isError={liveQuery.isError}
              limit={limit}
            />
          </section>

          <section>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-base font-semibold text-foreground">On Demand</h3>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs text-action hover:bg-action hover:text-action-hover"
                onClick={() => onSeeAll(PageRoutes.OnDemand)}
              >
                See All
                <ChevronRight className="ml-0.5 size-3" />
              </Button>
            </div>
            <ScheduleTable
              games={vodQuery.data ?? []}
              isLoading={vodQuery.isLoading}
              isError={vodQuery.isError}
              limit={limit}
            />
          </section>
        </div>

        {/* Right: filters */}
        <div className="flex flex-col gap-4 bg-popover p-4">
          {isAuthenticated && (favoritesQuery.data?.length ?? 0) > 0 && (
            <section>
              <div className="mb-1.5 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground">Favorites</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-[13px] text-action hover:bg-action hover:text-action-hover"
                  onClick={onManageFavorites}
                >
                  <Settings className="mr-1 size-4" />
                  Manage
                </Button>
              </div>
              <div className="flex flex-col gap-[5px]">
                {favoritesQuery.data?.map((team) => {
                  const id = String(team.team_id);
                  const checked = selectedTeams.includes(id);
                  return (
                    <div
                      key={team.team_id}
                      className="flex items-center gap-2 rounded px-1 py-0.5"
                    >
                      <Checkbox
                        id={`${idPrefix}-team-${id}`}
                        checked={checked}
                        onCheckedChange={() => toggleFilter("team", id)}
                        className="shrink-0"
                      />
                      {/* v1's global `label { display: inline-block }` leaks into
                          .v2 and outranks Tailwind's display utilities (it is
                          unlayered CSS), which drops the logo onto its own line.
                          Keeping the row layout on an inner span sidesteps that. */}
                      <label
                        htmlFor={`${idPrefix}-team-${id}`}
                        className="m-0 min-w-0 flex-1 cursor-pointer"
                      >
                        <span className="flex min-w-0 items-center gap-2">
                          <Avatar className="size-5 shrink-0 rounded-full">
                            {team.team_logo_url ? (
                              <AvatarImage
                                src={team.team_logo_url}
                                alt={team.team_name ?? "team"}
                              />
                            ) : null}
                            <AvatarFallback className="rounded-full bg-muted text-[8px]">
                              {(team.team_name ?? "?").charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="truncate text-[13px] capitalize text-foreground">
                            {team.team_name} {team.team_mascot}
                          </span>
                        </span>
                      </label>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          <section>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-base font-semibold text-foreground">Channels</h3>
              {!filters.channel && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs text-action hover:bg-action hover:text-action-hover"
                  onClick={() => onSeeAll(PageRoutes.UpcomingEvent)}
                >
                  See All
                  <ChevronRight className="ml-0.5 size-3" />
                </Button>
              )}
            </div>
            <div className="flex max-h-[220px] flex-col gap-[5px] overflow-y-auto">
              {channelsQuery.isLoading ? (
                <div className="flex flex-col gap-1">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-2 px-1 py-0.5">
                      <div className="size-4 animate-pulse rounded bg-accent" />
                      <div className="h-3 w-20 animate-pulse rounded bg-accent" />
                    </div>
                  ))}
                </div>
              ) : (
                channelsQuery.data?.map((ch) => {
                  const id = String(ch.channel_id);
                  const checked = selectedChannels.includes(id);
                  return (
                    <div
                      key={ch.channel_id}
                      className="flex items-center gap-2 rounded px-1 py-0.5"
                    >
                      <Checkbox
                        id={`${idPrefix}-channel-${id}`}
                        checked={checked}
                        onCheckedChange={() => toggleFilter("channel", id)}
                      />
                      <label
                        htmlFor={`${idPrefix}-channel-${id}`}
                        className="m-0 min-w-0 flex-1 cursor-pointer"
                      >
                        <span className="flex min-w-0 items-center gap-2">
                          <Avatar className="size-5 shrink-0 rounded-full">
                            {ch.channel_logo_url ? (
                              <AvatarImage
                                src={ch.channel_logo_url}
                                alt={ch.channel_name}
                              />
                            ) : null}
                            <AvatarFallback className="rounded-full bg-muted text-[8px]">
                              {(ch.channel_name ?? "?").charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="truncate text-[13px] capitalize text-foreground">
                            {ch.channel_name}
                          </span>
                        </span>
                      </label>
                    </div>
                  );
                })
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
